import { useState, useCallback, Dispatch, SetStateAction } from 'react';
import {
  Employee,
  EmployeeStatus,
  Company,
} from '../types';
import {
  loadEmployees,
  upsertEmployee,
  deleteEmployee as dbDeleteEmployee,
  upsertAbsences,
  upsertDelays,
  upsertEarlyDepartures,
  upsertSituations,
  ensureCompany,
} from '../services/rhDataService';
import { getProbationPeriodFromHiring } from '../probationPeriod';
import { sortEmployeesByName } from '../utils/employeeSort';
import { sentenceCasePtBr, canonicalizeEmployeeRole } from '../utils/textFormat';
import { BOARD_INITIAL } from './appDefaults';
import type { CompanyNotificationPayload } from '../utils/employeeNotificationMessages';
import {
  buildAbsenceReasonEditNotification,
  buildDeletedEmployeeNotification,
  buildEmployeeEditNotifications,
  buildEmployeeMoveNotification,
  buildNewEmployeeNotifications,
} from '../utils/employeeNotificationMessages';

export interface UseBoardEmployeesParams {
  columns: { id: string; title: string; color?: string }[];
  afastamentoDates: Record<string, string>;
  setAfastamentoDates: Dispatch<SetStateAction<Record<string, string>>>;
  currentCompany: Company | null;
  currentCompanyId: string | null;
  getSB: () => ReturnType<typeof import('../services/postgresClient').getPostgresClient>;
  persistSettingIfAuthenticated: (key: string, value: unknown, companyId?: string) => Promise<void>;
  getVerifiedDate: () => Date;
  addLog: (action: string, details: string) => void;
  /** Exibe notificação persistente ao usuário (add = verde, delete = vermelho, info = neutro). */
  addNotification: (title: string, message: string, type: 'add' | 'delete' | 'info') => void;
  broadcastNotifications: (payloads: CompanyNotificationPayload[]) => Promise<void>;
}

/**
 * Estado e CRUD de colaboradores no board Kanban.
 * @param params - Colunas, empresa atual e callbacks de auditoria
 * @returns Items, mapa de colunas e handlers de persistência
 */
export function useBoardEmployees({
  columns,
  afastamentoDates,
  setAfastamentoDates,
  currentCompany,
  currentCompanyId,
  getSB,
  persistSettingIfAuthenticated,
  getVerifiedDate,
  addLog,
  addNotification,
  broadcastNotifications,
}: UseBoardEmployeesParams) {
  const [items, setItems] = useState<Record<string, Employee[]>>(BOARD_INITIAL.items);
  const [columnKeyMap, setColumnKeyMap] = useState<Record<string, string>>({});

  const resetEmployeeState = useCallback(() => {
    setItems(BOARD_INITIAL.items);
    setColumnKeyMap({});
  }, []);

  const handleSaveEmployee = useCallback(
    async (
      data: Partial<Employee> & { columnId: string },
      editingEmployee: Employee | null,
      closeModal: () => void,
    ) => {
      const { columnId, ...employeeData } = data;
      const targetColTitle = columns.find((c) => c.id === columnId)?.title || columnId;
      const sb = getSB();

      if (editingEmployee) {
        const oldColId = Object.keys(items).find((key) =>
          items[key].some((e) => e.id === editingEmployee.id),
        );
        if (oldColId && oldColId !== columnId) {
          const fromColTitle = columns.find((c) => c.id === oldColId)?.title || oldColId;
          addLog('Movimentação', `Moveu ${data.name} de "${fromColTitle}" para "${targetColTitle}"`);
          const awayCols = ['afastados', 'afastados_adm'];
          if (awayCols.includes(columnId) && !awayCols.includes(oldColId)) {
            const newDates = {
              ...afastamentoDates,
              [editingEmployee.id]: getVerifiedDate().toISOString(),
            };
            setAfastamentoDates(newDates);
            if (sb && currentCompany) {
              void persistSettingIfAuthenticated('afastamento_dates', newDates, currentCompany);
            }
          } else if (!awayCols.includes(columnId) && awayCols.includes(oldColId)) {
            const newDates = { ...afastamentoDates };
            delete newDates[editingEmployee.id];
            setAfastamentoDates(newDates);
            if (sb && currentCompany) {
              void persistSettingIfAuthenticated('afastamento_dates', newDates, currentCompany);
            }
          }
        }

        const merged = { ...editingEmployee, ...employeeData } as Employee;
        const updatedEmp: Employee = {
          ...merged,
          role: canonicalizeEmployeeRole(merged.role || 'Função'),
          costCenterDescription:
            merged.costCenterDescription != null && merged.costCenterDescription !== ''
              ? sentenceCasePtBr(merged.costCenterDescription)
              : merged.costCenterDescription,
          probationPeriod: getProbationPeriodFromHiring(merged.hiringDate, getVerifiedDate()),
        };

        if (sb) {
          try {
            const companyId = currentCompanyId ?? (await ensureCompany(sb, currentCompany!));
            let colUuid = columnKeyMap[columnId];
            if (!colUuid) {
              const loaded = await loadEmployees(sb, companyId);
              setColumnKeyMap(loaded.columnKeyMap);
              colUuid = loaded.columnKeyMap[columnId];
              if (!colUuid) throw new Error('Falha ao sincronizar coluna no banco.');
            }
            if (colUuid) {
              await upsertEmployee(sb, updatedEmp, columnId, companyId, columnKeyMap);
              if (updatedEmp.absences && updatedEmp.absences.length >= 0) {
                await upsertAbsences(sb, updatedEmp.id, updatedEmp.absences);
              }
              await upsertDelays(sb, updatedEmp.id, updatedEmp.delays ?? []);
              await upsertEarlyDepartures(sb, updatedEmp.id, updatedEmp.earlyDepartures ?? []);
              await upsertSituations(sb, updatedEmp.id, updatedEmp.situations ?? []);
            }
          } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Erro de conexão';
            console.error('[Postgres] Erro ao salvar funcionário:', err);
            addNotification(
              'Erro ao Salvar',
              `Falha ao salvar colaboração no banco: ${message}`,
              'delete',
            );
            closeModal();
            return;
          }
        }

        const editPayloads = buildEmployeeEditNotifications(
          editingEmployee,
          updatedEmp,
          targetColTitle,
        );
        if (oldColId && oldColId !== columnId) {
          const fromColTitle = columns.find((c) => c.id === oldColId)?.title || oldColId;
          editPayloads.unshift(
            buildEmployeeMoveNotification(updatedEmp.name, fromColTitle, targetColTitle),
          );
        }
        void broadcastNotifications(editPayloads);

        setItems((prev) => {
          const next = { ...prev };
          if (oldColId) {
            if (oldColId !== columnId) {
              next[oldColId] = next[oldColId].filter((e) => e.id !== editingEmployee.id);
              next[columnId] = sortEmployeesByName([updatedEmp, ...(next[columnId] || [])]);
            } else {
              next[oldColId] = sortEmployeesByName(
                next[oldColId].map((e) => (e.id === editingEmployee.id ? updatedEmp : e)),
              );
            }
          }
          return next;
        });
      } else {
        const tempEmployee: Employee = {
          id: `emp-${Date.now()}`,
          name: employeeData.name || 'Novo Funcionário',
          role: canonicalizeEmployeeRole(employeeData.role || 'Função'),
          avatar: '',
          status: EmployeeStatus.Available,
          absences: employeeData.absences || [],
          ...employeeData,
        };
        const newEmployeeBase: Employee = {
          ...tempEmployee,
          role: canonicalizeEmployeeRole(tempEmployee.role || 'Função'),
          costCenterDescription:
            tempEmployee.costCenterDescription != null && tempEmployee.costCenterDescription !== ''
              ? sentenceCasePtBr(tempEmployee.costCenterDescription)
              : tempEmployee.costCenterDescription,
          probationPeriod: getProbationPeriodFromHiring(tempEmployee.hiringDate, getVerifiedDate()),
        };

        let newEmployee = newEmployeeBase;
        if (sb) {
          try {
            const companyId = currentCompanyId ?? (await ensureCompany(sb, currentCompany!));
            let colMap = { ...columnKeyMap };
            let colUuid = colMap[columnId];
            if (!colUuid) {
              const loaded = await loadEmployees(sb, companyId);
              setColumnKeyMap(loaded.columnKeyMap);
              colMap = loaded.columnKeyMap;
              colUuid = colMap[columnId];
              if (!colUuid) {
                throw new Error(
                  'A coluna selecionada não existe no banco. Tente dar F5 para sincronizar.',
                );
              }
            }
            const realId = await upsertEmployee(sb, tempEmployee, columnId, companyId, colMap);
            newEmployee = { ...newEmployeeBase, id: realId };
            if (newEmployee.absences && newEmployee.absences.length > 0) {
              await upsertAbsences(sb, realId, newEmployee.absences);
            }
            if (newEmployee.delays && newEmployee.delays.length > 0) {
              await upsertDelays(sb, realId, newEmployee.delays);
            }
            if (newEmployee.earlyDepartures && newEmployee.earlyDepartures.length > 0) {
              await upsertEarlyDepartures(sb, realId, newEmployee.earlyDepartures);
            }
            if (newEmployee.situations && newEmployee.situations.length > 0) {
              await upsertSituations(sb, realId, newEmployee.situations);
            }
          } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Erro de conexão';
            console.error('[Postgres] Erro ao inserir funcionário:', err);
            addNotification('Erro ao Inserir', `Falha ao adicionar: ${message}`, 'delete');
            closeModal();
            return;
          }
        }

        addLog('Adicionou Colaborador', `Colaborador: ${newEmployee.name} em "${targetColTitle}"`);
        const emptyEmployee: Employee = {
          ...newEmployee,
          absences: [],
          delays: [],
          earlyDepartures: [],
          situations: [],
        };
        void broadcastNotifications([
          ...buildNewEmployeeNotifications(newEmployee.name, targetColTitle),
          ...buildEmployeeEditNotifications(emptyEmployee, newEmployee, targetColTitle),
        ]);
        setItems((prev) => ({
          ...prev,
          [columnId]: sortEmployeesByName([newEmployee, ...(prev[columnId] || [])]),
        }));
      }
      closeModal();
    },
    [
      items,
      columns,
      columnKeyMap,
      afastamentoDates,
      currentCompany,
      currentCompanyId,
      getSB,
      getVerifiedDate,
      persistSettingIfAuthenticated,
      addLog,
      addNotification,
      broadcastNotifications,
      setAfastamentoDates,
    ],
  );

  const handleUpdateAbsence = useCallback(
    async (employeeId: string, absenceId: string, newReason: string) => {
      const allEmployees = Object.values(items).flat() as Employee[];
      const emp = allEmployees.find((e) => e.id === employeeId);
      const abs = emp?.absences?.find((a) => a.id === absenceId);
      if (!emp || !abs || abs.reason === newReason) return;

      addLog('Editou Falta', `Colaborador: ${emp.name} (Novo motivo: ${newReason})`);
      const updatedAbsences = (emp.absences || []).map((a) =>
        a.id === absenceId ? { ...a, reason: newReason } : a,
      );

      setItems((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((key) => {
          next[key] = next[key].map((e) => {
            if (e.id === employeeId && e.absences) {
              return {
                ...e,
                absences: e.absences.map((a) =>
                  a.id === absenceId ? { ...a, reason: newReason } : a,
                ),
              };
            }
            return e;
          });
        });
        return next;
      });

      const sb = getSB();
      if (sb) {
        upsertAbsences(sb, employeeId, updatedAbsences)
          .then(() => {
            void broadcastNotifications([
              buildAbsenceReasonEditNotification(emp.name, { ...abs, reason: newReason }),
            ]);
          })
          .catch((err: unknown) => {
            const message = err instanceof Error ? err.message : 'Erro de conexão';
            console.error('[Postgres] Erro ao editar motivo de falta:', err);
            addNotification('Erro ao Editar Falta', `Falha ao salvar motivo no banco: ${message}`, 'delete');
            // Reverte o estado local para o valor original (o banco não foi atualizado)
            setItems((prev) => {
              const next = { ...prev };
              Object.keys(next).forEach((key) => {
                next[key] = next[key].map((e) => {
                  if (e.id === employeeId && e.absences) {
                    return {
                      ...e,
                      absences: e.absences.map((a) =>
                        a.id === absenceId ? { ...a, reason: abs.reason } : a,
                      ),
                    };
                  }
                  return e;
                });
              });
              return next;
            });
          });
      }
    },
    [items, addLog, getSB, broadcastNotifications, addNotification],
  );

  const confirmDelete = useCallback(
    async (employeeToDelete: string | null, clearDeleteTarget: () => void) => {
      if (!employeeToDelete) return;
      const id = employeeToDelete;
      clearDeleteTarget();

      let empToDelete: Employee | null = null;
      let empColumnKey = '';
      for (const [key, emps] of Object.entries(items)) {
        const found = emps.find((e) => e.id === id);
        if (found) {
          empToDelete = found;
          empColumnKey = key;
          break;
        }
      }
      if (!empToDelete) return;

      const name = empToDelete.name;
      const sector = columns.find((c) => c.id === empColumnKey)?.title || empColumnKey;
      const sb = getSB();
      if (sb) {
        try {
          await dbDeleteEmployee(sb, id);
        } catch (err: unknown) {
          const message = err instanceof Error ? err.message : 'Erro de conexão';
          console.error('[Postgres] Erro ao deletar funcionário:', err);
          addNotification(
            'Erro ao Excluir',
            `Falha ao remover ${name} do banco: ${message}`,
            'delete',
          );
          return;
        }
      }

      setItems((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((key) => {
          next[key] = next[key].filter((emp) => emp.id !== id);
        });
        return next;
      });

      addLog('Deletou Colaborador', `Colaborador: ${name}`);
      void broadcastNotifications([buildDeletedEmployeeNotification(name, sector)]);
    },
    [items, columns, getSB, addLog, broadcastNotifications],
  );

  return {
    items,
    setItems,
    columnKeyMap,
    setColumnKeyMap,
    handleSaveEmployee,
    handleUpdateAbsence,
    confirmDelete,
    resetEmployeeState,
  };
}
